/************************************************************************
 ** IBM Confidential
 **
 ** IBM Business Analytics: Rapidly Adaptive Visualization Engine
 **
 ** (C) Copyright IBM Corp. 2024
 **
 ** The source code for this program is not published or otherwise divested of its trade secrets, 
 ** irrespective of what has been deposited with the U.S. Copyright Office.
 ************************************************************************/
// GENERATED
import BaseMoserObject from './BaseMoserObject';
import ModuleUtils from './ModuleUtils';
import MoserObject from './MoserObject';
import InsertFeedback from './InsertFeedback';
import InsertRecord from './InsertRecord';
import InsertStatus from './InsertStatus';
import MoserObjectUtils from './MoserObjectUtils';
import QuerySubjectUtils from './QuerySubjectUtils';
import ShortcutUtils from './ShortcutUtils';
import TransientSourceType from './TransientSourceType';
import InstanceType from './InstanceType';
import UseSpecType from './UseSpecType';
import EditModeType from './EditModeType';
import PropertySetter from './PropertySetter';
import ObjectFactory from './ObjectFactory';
import EditModeUtils from './EditModeUtils';
import ReferenceResolver from './ReferenceResolver';
import TreatAs from './TreatAs';
//@import BaseMoserObject (loadtime) // superclass
//@import com/ibm/bi/platform/moser/core/module/util/ModuleUtils (static) // getSourceModuleUseSpecInParentModule, getReferencedObjectsIdentifiers, updateQuerySubjectRef, canMarkAsTransientRel, updateThisObjectReference, findRelationshipMatch, getTreeChildrenImp, remove, createQIMap, insertQueryItem, suggestRelationships, getQuerySubjectBySourceQuerySubject, getLinkedQuerySubject, containsQSRefInOriginalRefThenRef, getIdGenerator, insertPackage, insertFolderItem, getSourceModuleAliasIfNotDefined, isPackage, getQuerySubjectById, updateOtherObjectReference, getTreeChildren, getParentQuerySubject, restoreItemNormalizationFromSourceQS, insertFolder
//@import com/ibm/bi/platform/moser/common/generated/metadata/MoserObject (static) // getModuleObject
//@import com/ibm/bi/platform/moser/core/module/util/InsertFeedback (static) // new
//@import com/ibm/bi/platform/moser/core/module/util/InsertRecord (static) // new
//@import com/ibm/bi/platform/moser/core/module/util/InsertStatus (static) // InsertStatus
//@import com/ibm/bi/platform/moser/common/utils/MoserObjectUtils (static) // getList, getQueryItems
//@import com/ibm/bi/platform/moser/core/module/util/QuerySubjectUtils (static) // clearEmbeddedFilter, clearPropertyOverride, getParentModule, getParentQuerySubject, getQueryItemById
//@import com/ibm/bi/platform/moser/common/utils/ShortcutUtils (static) // isShortcutValid, insertAsShortcut, isShortcutOrAliasValid
//@import com/ibm/bi/platform/moser/common/generated/metadata/TransientSourceType (static) // TransientSourceType
//@import com/ibm/bi/platform/moser/common/generated/metadata/InstanceType (static) // InstanceType
//@import com/ibm/bi/platform/moser/common/generated/metadata/UseSpecType (static) // UseSpecType
//@import com/ibm/bi/platform/moser/common/generated/metadata/EditModeType (static) // EditModeType
//@import com/ibm/bi/platform/moser/common/generated/metadata/PropertySetter (static) // set, setTaxonomy
//@import com/ibm/bi/platform/moser/common/generated/metadata/ObjectFactory (static) // new
//@import com/ibm/bi/platform/moser/common/utils/EditModeUtils (static) // setEditMode
//@import com/ibm/bi/platform/moser/common/utils/ReferenceResolver (static) // resolveRelationship
//@import com/ibm/bi/platform/moser/common/generated/metadata/TreatAs (static) // TreatAs

/**
 * This class provides a set of static utility APIs for inserting the MoserObjects. This class will be translated to JavaScript
 * @author  Zhuoheng Huang
 */
export default class InsertUtils extends BaseMoserObject{

	static _getAllowedChildrenTypes() {
		return ["Module", "FolderType", "Folder", "QuerySubject", "QueryItem", "Calculation", "Relationship", "Filter"];
	}

	static _getPropertiesFeatureID() {
		var propertiesFeatureIDs = [141, 192, 196, 194, 140, 198];
		var res = [];
		for (var __i_enFor0 = 0, __len_enFor0 = propertiesFeatureIDs.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var n = propertiesFeatureIDs[__i_enFor0];
			res.push(n);
		}
		return res;
	}

	static _isInvalidDest(objsToInsert, destObj) {
		if (!destObj) {
			return true;
		}
		var destObjType = destObj.getObjectType();
		if ("ItemType" == destObjType) {
			return true;
		}
		if ("QuerySubject" == destObjType) {
			var destQS = destObj;
			for (var __i_enFor0 = 0, __exp_enFor0 = objsToInsert, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var obj = __exp_enFor0[__i_enFor0];
				var srcQS = null;
				if ("QueryItem" == obj.getObjectType()) {
					srcQS = ModuleUtils._getParentQuerySubject(obj);
				}
				if ("QuerySubject" == obj.getObjectType()) {
					srcQS = obj;
				}
				if (!srcQS || !(InsertUtils._isReference(srcQS, destQS))) {
					return true;
				}
			}
			return false;
		}
		if ("QueryItem" == destObjType || "Folder" == destObjType) {
			var destQS = ModuleUtils._getParentQuerySubject(destObj);
			for (var __i_enFor1 = 0, __exp_enFor1 = objsToInsert, __len_enFor1 = __exp_enFor1.length;
					__i_enFor1 < __len_enFor1; ++__i_enFor1) {
				var obj = __exp_enFor1[__i_enFor1];
				if (!("QueryItem" == obj.getObjectType())) {
					return true;
				}
				var srcQS = ModuleUtils._getParentQuerySubject(obj);
				if (!srcQS || !(InsertUtils._isReference(srcQS, destQS))) {
					return true;
				}
			}
		}
		return "Calculation" == destObjType || "Filter" == destObjType;
	}

	/**
	 * Return true if destQS is a reference to srcQS
	 * @param {QuerySubject} srcQS
	 * @param {QuerySubject} destQS
	 */
	static _isReference(srcQS, destQS) {
		var moduleAlias = ModuleUtils.getSourceModuleAliasIfNotDefined(null, MoserObject.getModuleObject(destQS), srcQS);
		if (moduleAlias == null) {
			return false;
		}
		var ref = moduleAlias + "." + srcQS.getIdentifier();
		return ModuleUtils._containsQSRefInOriginalRefThenRef(ref, destQS);
	}

	static _createInsertFeedbackInvalid() {
		return new InsertFeedback([], [], false, null);
	}

	static canInsertSrcObjs(objsToInsert, destObj, index) {
		if (InsertUtils._isInvalidDest(objsToInsert, destObj)) {
			return InsertUtils._createInsertFeedbackInvalid();
		}
		var byInsert = [];
		var byRef = [];
		var srcRlshpsByInsert = [];
		var qsListByInsert = [];
		var srcRlshpsByRef = [];
		var qsListByRef = [];
		var destModule = MoserObject.getModuleObject(destObj);
		InsertUtils.gatherInsertActions(objsToInsert, destObj, index, index, byInsert, destModule, srcRlshpsByInsert, qsListByInsert, true);
		InsertUtils.gatherByRefActions(objsToInsert, destObj, index, byRef, srcRlshpsByRef, qsListByRef);
		InsertUtils.addInsertSrcRelationshipRecord(byInsert, srcRlshpsByInsert, destObj, qsListByInsert);
		InsertUtils.addInsertSrcRelationshipRecord(byRef, srcRlshpsByRef, destObj, qsListByRef);
		return new InsertFeedback(byInsert, byRef, InsertUtils._isAmbiguous(byInsert, byRef), index);
	}

	static addInsertSrcRelationshipRecord(actions, srcRlshps, destObj, qsList) {
		for (var __i_enFor0 = 0, __exp_enFor0 = srcRlshps, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var srcRshlp = __exp_enFor0[__i_enFor0];
			var add = InsertUtils.canInsertSrcRelationship(srcRshlp, destObj, qsList);
			if (add) {
				actions.push(new InsertRecord(srcRshlp, null, destObj, add, 0));
			}
		}
	}

	static canInsertModule(srcModule, destObj) {
		return InsertUtils._canInsertSrcModuleOrFolderType(srcModule, destObj);
	}

	static canInsertSrcFolderType(srcFolderType, destObj) {
		return InsertUtils._canInsertSrcModuleOrFolderType(srcFolderType, destObj);
	}

	/**
	 * @param  srcQI
	 * @param {MoserObject} destObj     can be a folder clone in which case the parent is null.
	 * @param {Module} destModule  must pass dest module. cannot get info from destObj.
	 * @param {Array} qsList
	 */
	static canAddSrcQuerySubject(srcQS, destObj, destModule, qsList) {
		var destObjType = destObj.getObjectType();
		var isDestModule = "Module" == destObjType;
		var isDestFolder = "FolderType" == destObjType;
		if (isDestModule || isDestFolder) {
			if (InsertUtils.isSourceOfTypeModule(srcQS, destModule) && !(InsertUtils._findReferences(srcQS, destModule).length==0)) {
				return null;
			}
			if (qsList) {
				qsList.push(srcQS.getIdentifier());
			}
			return InsertStatus.ADD;
		}
		return null;
	}

	/**
	 * @param {QueryItem} srcQI
	 * @param {MoserObject} destObj     can be a folder clone in which case the parent is null.
	 * @param {Module} destModule  must pass dest module. cannot get info from destObj.
	 */
	static canAddSrcQueryItem(srcQI, destObj, destModule) {
		var destObjType = destObj.getObjectType();
		var srcParentQS = ModuleUtils._getParentQuerySubject(srcQI);
		var destParentQS = ModuleUtils._getParentQuerySubject(destObj);
		if (InsertUtils.isSourceOfTypeModule(srcQI, destModule)) {
			return null;
		}
		if ("QuerySubject" == destObjType || "Folder" == destObjType) {
			var isMatching = InsertUtils._isMatchingSrcAndDestQS(destModule, srcParentQS, destParentQS);
			var hasSrcQI = false;
			var destQIs = MoserObjectUtils.getQueryItems(destParentQS);
			for (var __i_enFor0 = 0, __exp_enFor0 = destQIs, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var destQI = __exp_enFor0[__i_enFor0];
				if (destQI.getIdentifier() == srcQI.getIdentifier()) {
					hasSrcQI = true;
					break;
				}
			}
			if (isMatching && !hasSrcQI) {
				return InsertStatus.ADD;
			}
		}
		if ("Module" == destObjType || "FolderType" == destObjType) {
			return InsertStatus.ADD;
		}
		return null;
	}

	static canInsertSrcRelationship(srcRlshp, destObj, qsList) {
		var destObjType = destObj.getObjectType();
		if (!("Module" == destObjType)) {
			return null;
		}
		if (ModuleUtils._findRelationshipMatch(destObj, srcRlshp)) {
			return null;
		}
		var leftQS = srcRlshp.getLeft().getReferencedObject();
		var rightQS = srcRlshp.getRight().getReferencedObject();
		if (!leftQS || !rightQS) {
			return null;
		}
		var leftQsId = leftQS.getIdentifier();
		var rightQsId = rightQS.getIdentifier();
		var destModule = destObj;
		var srcParentModule = QuerySubjectUtils.getParentModule(srcRlshp);
		var prModuleUseSpecs = srcParentModule.getUseSpec();
		var asReference = !(prModuleUseSpecs.length==0);
		var leftDestQS = ModuleUtils.getQuerySubjectById(leftQsId, destModule);
		var rightDestQS = ModuleUtils.getQuerySubjectById(rightQsId, destModule);
		if (asReference) {
			if (!leftDestQS) {
				leftDestQS = ModuleUtils._getLinkedQuerySubject(leftQsId, srcParentModule, destModule);
			}
			if (!rightDestQS) {
				rightDestQS = ModuleUtils._getLinkedQuerySubject(rightQsId, srcParentModule, destModule);
			}
		}
		if (leftDestQS && rightDestQS) {
			if (ShortcutUtils.insertAsShortcut(leftQS) || ShortcutUtils.insertAsShortcut(rightQS)) {
				return null;
			}
			return InsertStatus.ADD;
		}
		var hasLeftQS = qsList.indexOf(leftQsId)> -1;
		var hasRightQS = qsList.indexOf(rightQsId)> -1;
		if (!hasLeftQS && !leftDestQS || !hasRightQS && !rightDestQS) {
			return null;
		}
		return InsertStatus.ADD;
	}

	static canInsertSrcCalculation(srcCalc, destObj) {
		return InsertUtils._canInsertSrcCalculationOrFilter(srcCalc, destObj);
	}

	static canInsertSrcFilter(srcFlt, destObj) {
		return InsertUtils._canInsertSrcCalculationOrFilter(srcFlt, destObj);
	}

	static _canInsertSrcCalculationOrFilter(srcObj, destObj) {
		var destObjType = destObj.getObjectType();
		if (ModuleUtils._getParentQuerySubject(srcObj)) {
			return null;
		}
		if ("FolderType" == destObjType || "Module" == destObjType) {
			return InsertStatus.ADD;
		}
		return null;
	}

	static _canInsertSrcModuleOrFolderType(srcObj, destObj) {
		var destObjType = destObj.getObjectType();
		if (!("Module" == destObjType || "FolderType" == destObjType)) {
			return null;
		}
		var destParentModule = QuerySubjectUtils.getParentModule(destObj);
		var isModule = "Module" == srcObj.getObjectType();
		if (ModuleUtils._isPackage(srcObj)) {
			return isModule ? InsertUtils._canAddModulePkg(srcObj, destParentModule) : InsertStatus.ADD;
		}
		return isModule ? null : InsertStatus.ADD;
	}

	static _canAddModulePkg(srcPkg, destParentModule) {
		var mdtvTree = destParentModule.getMetadataTreeView()[0].getFolderItem();
		for (var __i_enFor0 = 0, __exp_enFor0 = mdtvTree, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var item = __exp_enFor0[__i_enFor0];
			var ftItem = item.getFolder();
			if (ftItem && TransientSourceType.PACKAGE === ftItem.getTransientSource()) {
				if (ftItem.getLabel() == srcPkg.getLabel()) {
					return null;
				}
			}
		}
		return InsertStatus.ADD;
	}

	static _isMatchingSrcAndDestQS(destModule, srcQS, destQS) {
		var srcModuleAlias = ModuleUtils.getSourceModuleAliasIfNotDefined(null, destModule, srcQS);
		if (srcModuleAlias == null) {
			return false;
		}
		var expectedDestQsRef = srcModuleAlias + "." + srcQS.getIdentifier();
		var qsRefs = ModuleUtils._getReferencedObjectsIdentifiers(destQS);
		for (var __i_enFor0 = 0, __exp_enFor0 = qsRefs, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var qsRef = __exp_enFor0[__i_enFor0];
			if (expectedDestQsRef == qsRef) {
				return true;
			}
		}
		return false;
	}

	static _hasMissingQI(srcQS, destQS) {
		if (srcQS.basicGetItem()) {
			var qiMap = ModuleUtils._createQIMap(destQS);
			for (var __i_enFor0 = 0, __exp_enFor0 = MoserObjectUtils.getQueryItems(srcQS), __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var qi = __exp_enFor0[__i_enFor0];
				if (!qiMap[qi.getIdentifier()] || ShortcutUtils.isShortcutOrAliasValid(destQS)) {
					return true;
				}
			}
		}
		return false;
	}

	static _invalidInsertIndex(list, index) {
		return !list || index < 0 || index > list.length;
	}

	static gatherInsertActions$0(insertItems, destObj, initialIndex, index, actions, destModule, srcRlshps, qsList, isByInsertProcess) {
		InsertUtils._gatherInsertActions$1(insertItems, destObj, initialIndex, index, actions, destModule, srcRlshps, qsList, false, isByInsertProcess);
	}

	static _gatherInsertActions$1(insertItems, destObj, initialIndex, index, actions, destModule, srcRlshps, qsList, isChildrenCall, isByInsertProcess) {
		var indexForAdd = 0;
		if (index != null) {
			indexForAdd = index;
		} else {
			index = 0;
		}
		for (var __i_enFor0 = 0, __exp_enFor0 = insertItems, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var srcObj = __exp_enFor0[__i_enFor0];
			var srcObjType = srcObj.getObjectType();
			var srcObjClone = null;
			var add = InsertUtils._checkAdd(srcObj, destObj, indexForAdd, destModule, srcRlshps, qsList);
			if (add) {
				if ("FolderType" == srcObjType) {
					srcObjClone = srcObj.clone();
				}
				if (InsertUtils._isQIFromSameSrcQS(srcObj, destObj, actions)) {
					index--;
				}
				actions.push(new InsertRecord(srcObj, srcObjClone, destObj, add, index++));
			} else if (InsertUtils.isValidObjectTypesForUpdate(srcObj, destObj)) {
				if (initialIndex != null && !("Module" == destObj.getObjectType())) {
					continue;
				}
				var isDestObjQS = "QuerySubject" == destObj.getObjectType();
				if (isDestObjQS && InstanceType.REFERENCE == (destObj).getInstanceType()) {
					continue;
				}
				if (!isByInsertProcess) {
					var updateRecord = InsertUtils._checkUpdate(srcObj, destObj, isChildrenCall);
					if (updateRecord) {
						actions.push(updateRecord);
					}
				}
				var numofActions = actions.length;
				var itemsToDelete = InsertUtils._checkDelete(srcObj, destObj);
				for (var __i_enFor1 = 0, __exp_enFor1 = itemsToDelete, __len_enFor1 = __exp_enFor1.length;
						__i_enFor1 < __len_enFor1; ++__i_enFor1) {
					var item = __exp_enFor1[__i_enFor1];
					actions.push(new InsertRecord(item, null, destObj, InsertStatus.DELETE, null));
				}
				if ("QuerySubject" == srcObjType || "Folder" == srcObjType) {
					var childrenItems = ModuleUtils.getTreeChildren(srcObj, 1, InsertUtils._getAllowedChildrenTypes());
					InsertUtils._gatherInsertActions$1(childrenItems, destObj, initialIndex, index, actions, destModule, srcRlshps, qsList, true, isByInsertProcess);
					var numofActionsAfterChildren = actions.length;
					if (!isByInsertProcess && "QuerySubject" == srcObjType && numofActions == numofActionsAfterChildren) {
						actions.push(InsertUtils._createNoOpRecord(srcObj));
					}
				}
			}
			if ("Module" == srcObjType && !(ModuleUtils._isPackage(srcObj))) {
				var childrenItems = ModuleUtils.getTreeChildren(srcObj, 1, InsertUtils._getAllowedChildrenTypes());
				InsertUtils._gatherInsertActions$1(childrenItems, destObj, initialIndex, index, actions, destModule, srcRlshps, qsList, true, isByInsertProcess);
			}
			if ("FolderType" == srcObjType) {
				if (ModuleUtils._isPackage(srcObj) || !srcObjClone) {
					return;
				}
				var childrenItems = ModuleUtils.getTreeChildren(srcObj, 1, InsertUtils._getAllowedChildrenTypes());
				InsertUtils.gatherInsertActions$0(childrenItems, srcObjClone, initialIndex, 0, actions, destModule, srcRlshps, qsList, isByInsertProcess);
			}
		}
	}

	static gatherByRefActions(insertItems, destObj, index, actions, srcRlshps, qsList) {
		var destModule = MoserObject.getModuleObject(destObj);
		var insertItemsOfSourceModule = [];
		var insertItemsNotOfSourceModule = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = insertItems, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var srcObj = __exp_enFor0[__i_enFor0];
			if (InsertUtils.isSourceOfTypeModule(srcObj, destModule)) {
				insertItemsOfSourceModule.push(srcObj);
			} else {
				insertItemsNotOfSourceModule.push(srcObj);
			}
		}
		var qiAndQStoCheck = InsertUtils._getQIAndQSToCheck(insertItemsNotOfSourceModule);
		var validInsertItems = [];
		for (var __i_enFor1 = 0, __exp_enFor1 = qiAndQStoCheck, __len_enFor1 = __exp_enFor1.length;
				__i_enFor1 < __len_enFor1; ++__i_enFor1) {
			var srcObj = __exp_enFor1[__i_enFor1];
			if (InsertUtils.isValidObjectTypesForUpdate(srcObj, destObj)) {
				validInsertItems.push(srcObj);
			}
		}
		if (!(validInsertItems.length==0)) {
			var noRefQSList = [];
			var refs = InsertUtils._getReferencesToInsertObjectsMap(validInsertItems, destObj, destModule, noRefQSList);
			var count = 0;
			for (var __i_enFor2 = 0, __exp_enFor2 = refs[0], __len_enFor2 = __exp_enFor2.length;
					__i_enFor2 < __len_enFor2; ++__i_enFor2) {
				var srcItem = __exp_enFor2[__i_enFor2];
				var srcList = [];
				srcList.push(srcItem);
				var ref = refs[1][count];
				InsertUtils.gatherInsertActions(srcList, ref, null, 0, actions, destModule, null, null, false);
				count++;
			}
			if (!(actions.length==0) && !(noRefQSList.length==0)) {
				InsertUtils.gatherInsertActions(noRefQSList, destObj, index, index, actions, destModule, srcRlshps, qsList, true);
			}
		}
		if (!(insertItemsOfSourceModule.length==0)) {
			InsertUtils.gatherInsertActions(insertItemsOfSourceModule, destObj, index, index, actions, destModule, srcRlshps, qsList, true);
		}
	}

	static _isQIFromSameSrcQS(srcObj, destObj, actions) {
		var destObjType = destObj.getObjectType();
		if (!("QueryItem" == srcObj.getObjectType() && ("Module" == destObjType || "FolderType" == destObjType))) {
			return false;
		}
		if (!actions || actions.length==0) {
			return false;
		}
		var lastRecord = actions[actions.length - 1];
		var lastSrcObj = lastRecord.getObj();
		var lastDestObj = lastRecord.getParentObj();
		if (!lastSrcObj || !lastDestObj || !("QueryItem" == lastSrcObj.getObjectType())) {
			return false;
		}
		return QuerySubjectUtils.getParentQuerySubject(srcObj) == QuerySubjectUtils.getParentQuerySubject(lastSrcObj);
	}

	static _noDupByRefList(byInsert, byRef) {
		var byRefNoDup = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = byRef, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var insertRecordByRef = __exp_enFor0[__i_enFor0];
			var dup = false;
			for (var __i_enFor1 = 0, __exp_enFor1 = byInsert, __len_enFor1 = __exp_enFor1.length;
					__i_enFor1 < __len_enFor1; ++__i_enFor1) {
				var insertRecordByInsert = __exp_enFor1[__i_enFor1];
				if (insertRecordByRef.isDuplicate(insertRecordByInsert)) {
					dup = true;
				}
			}
			if (!dup) {
				byRefNoDup.push(insertRecordByRef);
			}
		}
		return byRefNoDup;
	}

	static _isAmbiguous(byInsert, byRef) {
		if (byInsert.length==0 || byRef.length==0) {
			return false;
		}
		var byRefNoDup = InsertUtils._noDupByRefList(byInsert, byRef);
		var ids = [];
		for (var __i_enFor0 = 0, __exp_enFor0 = byInsert, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var insertRecord = __exp_enFor0[__i_enFor0];
			var obj = insertRecord.getObj();
			if ("FolderType" == obj.getObjectType()) {
				ids.push((obj).getIdentifier());
			} else if ("Folder" == obj.getObjectType()) {
				ids.push((obj).getIdentifier());
			} else if ("QuerySubject" == obj.getObjectType()) {
				ids.push((obj).getIdentifier());
			} else if ("QueryItem" == obj.getObjectType()) {
				ids.push((obj).getIdentifier());
			}
		}
		for (var __i_enFor1 = 0, __exp_enFor1 = byRefNoDup, __len_enFor1 = __exp_enFor1.length;
				__i_enFor1 < __len_enFor1; ++__i_enFor1) {
			var insertRecord = __exp_enFor1[__i_enFor1];
			if (InsertUtils._isParentInByInsert(insertRecord.getObj(), ids)) {
				return true;
			}
		}
		return false;
	}

	static isSourceOfTypeModule(srcObj, destModule) {
		var refUseSpec = ModuleUtils.getSourceModuleUseSpecInParentModule(srcObj, destModule);
		return refUseSpec && UseSpecType.MODULE == refUseSpec.getType();
	}

	static isValidObjectTypesForUpdate(srcObj, destObj) {
		var srcObjType = srcObj.getObjectType();
		var destObjType = destObj.getObjectType();
		if ("QuerySubject" == srcObjType) {
			return "QuerySubject" == destObjType || "Module" == destObjType && !(ModuleUtils._isPackage(MoserObject.getModuleObject(srcObj))) || "FolderType" == destObjType;
		}
		if ("QueryItem" == srcObjType) {
			return "QuerySubject" == destObjType || "QueryItem" == destObjType || "Folder" == destObjType || "Module" == destObjType || "FolderType" == destObjType;
		}
		return false;
	}

	static _checkAdd(srcObj, destObj, index, destModule, srcRlshps, qsList) {
		var destObjChildren = ModuleUtils.getTreeChildren(destObj, 1, InsertUtils._getAllowedChildrenTypes());
		if (InsertUtils._invalidInsertIndex(destObjChildren, index)) {
			return null;
		}
		var srcObjType = srcObj.getObjectType();
		switch (srcObjType) {
		case "Module":
			return InsertUtils.canInsertModule(srcObj, destObj);
		case "FolderType":
			return InsertUtils.canInsertSrcFolderType(srcObj, destObj);
		case "QuerySubject":
			return InsertUtils.canAddSrcQuerySubject(srcObj, destObj, destModule, qsList);
		case "QueryItem":
			return InsertUtils.canAddSrcQueryItem(srcObj, destObj, destModule);
		case "Calculation":
			return InsertUtils.canInsertSrcCalculation(srcObj, destObj);
		case "Filter":
			return InsertUtils.canInsertSrcFilter(srcObj, destObj);
		case "Relationship":
			if (srcRlshps) {
				srcRlshps.push(srcObj);
			}
		default:
			return null;
		}
	}

	static _checkUpdate(srcObj, destObj, isChildrenCall) {
		if (!("QueryItem" == srcObj.getObjectType())) {
			return null;
		}
		if (!("QueryItem" == destObj.getObjectType() || "QuerySubject" == destObj.getObjectType())) {
			return null;
		}
		var destParentQS = ModuleUtils._getParentQuerySubject(destObj);
		if (EditModeType.RO === destParentQS.getEditMode()) {
			return null;
		}
		var srcQI = srcObj;
		var destQI;
		if ("QuerySubject" == destObj.getObjectType()) {
			destQI = QuerySubjectUtils.getQueryItemById((srcObj).getIdentifier(), destObj);
		} else if ("QueryItem" == destObj.getObjectType()) {
			destQI = destObj;
		}
		if (!destQI) {
			return null;
		}
		var destQIExpression = destQI.getExpression();
		var destQIId = destQI.getIdentifier();
		if (destQIExpression == null || !(destQIExpression == destQIId)) {
			return null;
		}
		return InsertUtils._checkQIPropertyUpdate(srcQI, destQI, isChildrenCall);
	}

	static _checkQIPropertyUpdate(srcQI, destQI, isChildrenCall) {
		var insertRecord = new InsertRecord();
		var allowedProperties = InsertUtils._getPropertiesFeatureID();
		var userChangedProps = destQI.getPropertyOverride();
		for (var __i_enFor0 = 0, __exp_enFor0 = allowedProperties, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var featureID = __exp_enFor0[__i_enFor0];
			if (userChangedProps && userChangedProps.indexOf(srcQI.getFeatureName(featureID))> -1) {
				continue;
			}
			var srcQIProp = srcQI.get(featureID);
			var destQIProp = destQI.get(featureID);
			if (userChangedProps && userChangedProps.indexOf(srcQIProp)> -1) {
				continue;
			}
			if (198 == featureID) {
				var srcTaxonomyList = srcQIProp;
				var dmTaxonomyList = destQIProp;
				if (!(InsertUtils._taxonomyEquals(srcTaxonomyList, dmTaxonomyList))) {
					insertRecord.addPropertiesUpdate(srcQI.getFeatureName(featureID));
				}
			} else if (srcQIProp != null && !(srcQIProp === destQIProp)) {
				insertRecord.addPropertiesUpdate(srcQI.getFeatureName(featureID));
			}
		}
		if (!(insertRecord.getPropertiesUpdate().length==0)) {
			insertRecord.setObj(destQI);
			insertRecord.setSrcObjForUpdate(srcQI);
			insertRecord.setInsertStatus(InsertStatus.UPDATE);
			return insertRecord;
		}
		if (!isChildrenCall) {
			return InsertUtils._createNoOpRecord(srcQI);
		}
		return null;
	}

	static _taxonomyEquals(srcTaxonomyList, dmTaxonomyList) {
		var srcTaxonomyIsEmpty = !srcTaxonomyList || srcTaxonomyList.length==0;
		var dmTaxonomyIsEmpty = !dmTaxonomyList || dmTaxonomyList.length==0;
		if (srcTaxonomyIsEmpty && dmTaxonomyIsEmpty) {
			return true;
		}
		if (srcTaxonomyIsEmpty != dmTaxonomyIsEmpty) {
			return false;
		}
		var srcTaxonomy = srcTaxonomyList[0];
		var dmTaxonomy = dmTaxonomyList[0];
		var features = srcTaxonomy.getFeatureList(null);
		for (var __i_enFor0 = 0, __exp_enFor0 = features, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var feature = __exp_enFor0[__i_enFor0];
			var srcValue = srcTaxonomy.get(feature.getFeatureID());
			var dmValue = dmTaxonomy.get(feature.getFeatureID());
			if (srcValue == null && dmValue == null) {
				continue;
			}
			if (srcValue != null && !(srcValue === dmValue)) {
				return false;
			}
			if (!(dmValue === srcValue)) {
				return false;
			}
		}
		return true;
	}

	static _checkDelete(srcObj, destObj) {
		var itemsToDelete = [];
		if (!("QuerySubject" == srcObj.getObjectType()) || !("QuerySubject" == destObj.getObjectType())) {
			return itemsToDelete;
		}
		var srcQS = srcObj;
		var destQS = destObj;
		if (!(InsertUtils._isMatchingSrcAndDestQS(QuerySubjectUtils.getParentModule(destObj), srcQS, destQS))) {
			return itemsToDelete;
		}
		var destQIs = MoserObjectUtils.getQueryItems(destObj);
		for (var __i_enFor0 = 0, __exp_enFor0 = destQIs, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var qi = __exp_enFor0[__i_enFor0];
			var id = qi.getIdentifier();
			if (id != null && !(qi.getExpression() == id)) {
				continue;
			}
			if (!QuerySubjectUtils.getQueryItemById(id, srcQS)) {
				itemsToDelete.push(qi);
			}
		}
		return itemsToDelete;
	}

	static _getQIAndQSToCheck(itemsToInsert) {
		var itemsToCheck = MoserObjectUtils.getList(null);
		var nestedQIs = MoserObjectUtils.getList(null);
		var qiToAdd = MoserObjectUtils.getList(null);
		var qiMap = {};
		for (var __i_enFor0 = 0, __exp_enFor0 = itemsToInsert, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var item = __exp_enFor0[__i_enFor0];
			var itemType = item.getObjectType();
			if ("QuerySubject" == itemType) {
				itemsToCheck.push(item);
				Array.prototype.push.apply(nestedQIs, InsertUtils._getChildrenQIIds(MoserObjectUtils.getQueryItems(item)));
			} else if ("QueryItem" == itemType) {
				var qi = item;
				qiMap[qi.getIdentifier()] = qi;
				qiToAdd.push(qi.getIdentifier());
			} else if ("Module" == itemType) {
				Array.prototype.push.apply(itemsToCheck, (item).getQuerySubject());
			} else if ("FolderType" == itemType) {
				Array.prototype.push.apply(itemsToCheck, ModuleUtils._getTreeChildrenImp(item, 2147483647, ["QuerySubject"], true, false));
			} else if ("Folder" == itemType) {
				Array.prototype.push.apply(nestedQIs, InsertUtils._getChildrenQIIds(MoserObjectUtils.getQueryItems((item).getItem())));
			}
		}
		for (var __i_enFor1 = 0, __exp_enFor1 = qiToAdd, __len_enFor1 = __exp_enFor1.length;
				__i_enFor1 < __len_enFor1; ++__i_enFor1) {
			var qiId = __exp_enFor1[__i_enFor1];
			if (!(nestedQIs.indexOf(qiId)> -1)) {
				itemsToCheck.push(qiMap[qiId]);
			}
		}
		return itemsToCheck;
	}

	static _getChildrenQIIds(QIs) {
		var childrenQIIds = MoserObjectUtils.getList(null);
		for (var __i_enFor0 = 0, __exp_enFor0 = QIs, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var qi = __exp_enFor0[__i_enFor0];
			childrenQIIds.push(qi.getIdentifier());
		}
		return childrenQIIds;
	}

	static _getReferencesToInsertObjectsMap(itemsToCheck, scope, destModule, noRefQSList) {
		var isScopeModule = false;
		var isScopeFolderType = false;
		if ("Module" == scope.getObjectType()) {
			isScopeModule = true;
		}
		if ("FolderType" == scope.getObjectType()) {
			isScopeFolderType = true;
		}
		var depth = 2147483647 - 1;
		var qsIdSet = {};
		if (isScopeFolderType) {
			var allQSInScope = ModuleUtils._getTreeChildrenImp(scope, depth, ["QuerySubject"], true, false);
			for (var __i_enFor0 = 0, __exp_enFor0 = allQSInScope, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var obj = __exp_enFor0[__i_enFor0];
				qsIdSet[(obj).getIdentifier()] = 1;
			}
		}
		var res = [];
		var srcItems = [];
		var refItems = [];
		for (var __i_enFor1 = 0, __exp_enFor1 = itemsToCheck, __len_enFor1 = __exp_enFor1.length;
				__i_enFor1 < __len_enFor1; ++__i_enFor1) {
			var item = __exp_enFor1[__i_enFor1];
			var refs = InsertUtils._findReferences(item, destModule);
			if (refs.length==0) {
				if ("QuerySubject" == item.getObjectType()) {
					noRefQSList.push(item);
				}
				continue;
			}
			for (var __i_enFor2 = 0, __exp_enFor2 = refs, __len_enFor2 = __exp_enFor2.length;
					__i_enFor2 < __len_enFor2; ++__i_enFor2) {
				var ref = __exp_enFor2[__i_enFor2];
				if (!isScopeModule && !isScopeFolderType) {
					break;
				}
				if (isScopeFolderType) {
					var refQS = ModuleUtils._getParentQuerySubject(ref);
					if (qsIdSet.hasOwnProperty(refQS.getIdentifier())) {
						srcItems.push(item);
						refItems.push(ref);
					}
				} else {
					srcItems.push(item);
					refItems.push(ref);
				}
			}
		}
		res.push(srcItems);
		res.push(refItems);
		return res;
	}

	static _findReferences(obj, destModule) {
		var refObjs = MoserObjectUtils.getList(null);
		var itemType = obj.getObjectType();
		if ("QuerySubject" == itemType) {
			Array.prototype.push.apply(refObjs, ModuleUtils.getQuerySubjectBySourceQuerySubject(obj, destModule, null));
		} else if ("QueryItem" == itemType) {
			var qi = obj;
			var parentQS = ModuleUtils._getParentQuerySubject(obj);
			var refQSList = ModuleUtils.getQuerySubjectBySourceQuerySubject(parentQS, destModule, null);
			for (var __i_enFor0 = 0, __exp_enFor0 = refQSList, __len_enFor0 = __exp_enFor0.length;
					__i_enFor0 < __len_enFor0; ++__i_enFor0) {
				var refQS = __exp_enFor0[__i_enFor0];
				var refQIList = MoserObjectUtils.getQueryItems(refQS);
				for (var __i_enFor1 = 0, __exp_enFor1 = refQIList, __len_enFor1 = __exp_enFor1.length;
						__i_enFor1 < __len_enFor1; ++__i_enFor1) {
					var refQI = __exp_enFor1[__i_enFor1];
					var qiIdentifier = qi.getIdentifier();
					if (qiIdentifier == refQI.getIdentifier() && qiIdentifier == refQI.getExpression()) {
						refObjs.push(refQI);
					}
				}
			}
		}
		return refObjs;
	}

	static _isParentInByInsert(obj, ids) {
		var objType = obj.getObjectType();
		if ("Module" == objType) {
			return false;
		}
		if (ids.indexOf((obj).getIdentifier())> -1) {
			return true;
		}
		var parentObj;
		if ("QuerySubject" == objType) {
			parentObj = obj;
		} else {
			parentObj = obj.getParent();
		}
		if ("ItemType" == parentObj.getObjectType()) {
			parentObj = parentObj.getParent();
		}
		if (ids.indexOf((parentObj).getIdentifier())> -1) {
			return true;
		}
		return InsertUtils._isParentInByInsert(parentObj.getParent(), ids);
	}

	/**
	 * Processes Insert Feedback and performs insert. Client should remove byRef or byInsert. Only one list should be defined.
	 * @param {InsertFeedback} insertFeedback 	InsertFeedback
	 * @throws {CloneNotSupportedException} CloneNotSupportedException
	 */
	static insertSrcObjs(insertFeedback) {
		if (!insertFeedback) {
			return;
		}
		var byInsertList = insertFeedback.getByInsert();
		var byRefList = insertFeedback.getByRef();
		var byInsertIsEmpty = !byInsertList || byInsertList.length==0;
		var byRefIsEmpty = !byRefList || byRefList.length==0;
		if (byInsertIsEmpty && byRefIsEmpty) {
			return;
		}
		var insertRecords = [];
		if (!(insertFeedback.getIsAmbiguous())) {
			if (!byInsertIsEmpty) {
				Array.prototype.push.apply(insertRecords, byInsertList);
			}
			if (!byInsertIsEmpty && !byRefIsEmpty) {
				Array.prototype.push.apply(insertRecords, InsertUtils._noDupByRefList(byInsertList, byRefList));
			} else if (!byRefIsEmpty) {
				Array.prototype.push.apply(insertRecords, byRefList);
			}
		} else {
			insertRecords = byInsertIsEmpty ? byRefList : byInsertList;
		}
		var initialIndex = insertFeedback.getInitialIndex();
		if (initialIndex == null) {
			initialIndex = 0;
		}
		var targetQSs = {};
		var newlyAddQs = [];
		var relList = [];
		var oldIdToObjectMap = {};
		var destModule;
		for (var __i_enFor0 = 0, __exp_enFor0 = insertRecords, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var record = __exp_enFor0[__i_enFor0];
			InsertUtils.insertSrcObjUpdate(record);
			InsertUtils.insertSrcObjDelete(record);
			var parentModule = InsertUtils.insertSrcObjAdd(record, initialIndex, targetQSs, relList, oldIdToObjectMap, newlyAddQs);
			if (!destModule) {
				destModule = parentModule;
			}
		}
		var suggestedRshlps = InsertUtils.suggestRelationships(newlyAddQs, destModule);
		Array.prototype.push.apply(suggestedRshlps, relList);
		for (var __i_enFor1 = 0, __exp_enFor1 = suggestedRshlps, __len_enFor1 = __exp_enFor1.length;
				__i_enFor1 < __len_enFor1; ++__i_enFor1) {
			var rel = __exp_enFor1[__i_enFor1];
			InsertUtils.insertSrcRelationship(rel, destModule, oldIdToObjectMap);
		}
	}

	/**
	 * Processes Insert Records with InsertStatus UPDATE. Performs an Update Object by updating and synchronizing a Moser object in the data module with its reference in the source module.
	 * @param {InsertRecord} record 	update insert record
	 * @param  includeInvalid  include query subjects pointing to an invalid target
	 * @throws {CloneNotSupportedException} CloneNotSupportedException
	 */
	static insertSrcObjUpdate(record) {
		if (InsertStatus.UPDATE != record.getInsertStatus()) {
			return;
		}
		var srcObj = record.getSrcObjForUpdate();
		var dmObj = record.getObj();
		var featureNames = record.getPropertiesUpdate();
		if (!("QueryItem" == dmObj.getObjectType())) {
			return;
		}
		var srcQI = srcObj;
		var dmQI = dmObj;
		for (var __i_enFor0 = 0, __exp_enFor0 = featureNames, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var featureName = __exp_enFor0[__i_enFor0];
			var featureId = srcObj.getFeatureID(featureName);
			if (198 == featureId) {
				var srcTaxonomyType = srcQI.get(featureId);
				if (srcTaxonomyType.length==0 && !(dmQI.getTaxonomy().length==0)) {
					dmQI.removeTaxonomyAt(0);
				} else if (!(srcTaxonomyType.length==0) && dmQI.getTaxonomy().length==0) {
					dmQI.addTaxonomy(srcTaxonomyType[0].clone());
				} else {
					InsertUtils.updateTaxonomyType(srcTaxonomyType[0], dmQI.getTaxonomy()[0]);
				}
			} else {
				PropertySetter.set(dmQI, featureId, srcQI.get(featureId), true);
			}
		}
	}

	static updateTaxonomyType(srcTaxonomyType, dmTaxonomyType) {
		var features = srcTaxonomyType.getFeatureList(null);
		for (var __i_enFor0 = 0, __exp_enFor0 = features, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var feature = __exp_enFor0[__i_enFor0];
			PropertySetter.setTaxonomy(dmTaxonomyType, feature.getFeatureID(), srcTaxonomyType.get(feature.getFeatureID()), true);
		}
	}

	static insertSrcObjDelete(record) {
		if (InsertStatus.DELETE != record.getInsertStatus()) {
			return;
		}
		ModuleUtils.remove(record.getObj());
	}

	static insertSrcObjAdd(rec, initialIndex, targetQSs, relList, oldIdToObjectMap, newlyAddQs) {
		if (InsertStatus.ADD != rec.getInsertStatus()) {
			return null;
		}
		if (!rec.getObj()) {
			return null;
		}
		var qsNewlyInsertQIMap = {};
		var oldIdList = [];
		var qsNewlyInsertQILst = [];
		var newObjects = [];
		InsertUtils.addSrcObjsImpl(rec.getObj(), rec.getObjClone(), rec.getParentObj(), rec.getIndex(), initialIndex, null, relList, oldIdList, oldIdToObjectMap, newObjects, qsNewlyInsertQIMap, qsNewlyInsertQILst, newlyAddQs, targetQSs);
		return QuerySubjectUtils.getParentModule(rec.getParentObj());
	}

	static addSrcObjsImpl(srcObj, srcObjClone, destObj, mdtvIndex, initialIndex, destMDTV, relList, oldIdList, oldIdToObjectMap, newObjects, qsNewlyInsertQIMap, qsNewlyInsertQILst, newlyAddQs, targetQSs) {
		var srcObjType = srcObj.getObjectType();
		switch (srcObjType) {
		case "QuerySubject":
			InsertUtils.insertSrcQuerySubject(srcObj, destObj, mdtvIndex, destMDTV, oldIdList, oldIdToObjectMap, newlyAddQs, newObjects);
			break;
		case "QueryItem":
			InsertUtils.insertSrcQueryItem(srcObj, destObj, mdtvIndex, initialIndex, oldIdList, oldIdToObjectMap, qsNewlyInsertQIMap, qsNewlyInsertQILst, targetQSs);
			break;
		case "Relationship":
			if (relList) {
				relList.push(srcObj);
			}
			break;
		case "Calculation":
			InsertUtils.insertSrcCalculation(srcObj, destObj, mdtvIndex, oldIdList, oldIdToObjectMap, newObjects);
			break;
		case "Filter":
			InsertUtils.insertSrcFilter(srcObj, destObj, mdtvIndex);
			break;
		case "FolderType":
			InsertUtils.insertSrcFolderType(srcObjClone, destObj, mdtvIndex, destMDTV);
			break;
		case "Module":
			InsertUtils.insertSrcModulePkg(srcObj, destObj, initialIndex, destMDTV);
			break;
		}
		ModuleUtils._restoreItemNormalizationFromSourceQS(qsNewlyInsertQILst, qsNewlyInsertQIMap, new ObjectFactory());
	}

	static suggestRelationships(newlyAddQs, destModule) {
		var srcIds = [];
		var srcIdToQsMap = {};
		var srcIdToModuleMap = {};
		for (var __i_enFor0 = 0, __exp_enFor0 = newlyAddQs, __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var qs = __exp_enFor0[__i_enFor0];
			var srcModule = QuerySubjectUtils.getParentModule(qs);
			var srcModuleId = ModuleUtils.getSourceModuleAliasIfNotDefined(null, destModule, qs);
			srcIds.push(srcModuleId);
			var qsList = srcIdToQsMap[srcModuleId];
			if (!qsList) {
				qsList = [];
				srcIdToQsMap[srcModuleId] = qsList;
			}
			qsList.push(qs);
			srcIdToModuleMap[srcModuleId] = srcModule;
		}
		var rshlipList = [];
		for (var __i_enFor1 = 0, __exp_enFor1 = srcIds, __len_enFor1 = __exp_enFor1.length;
				__i_enFor1 < __len_enFor1; ++__i_enFor1) {
			var srcId = __exp_enFor1[__i_enFor1];
			Array.prototype.push.apply(rshlipList, ModuleUtils.suggestRelationships(srcIdToModuleMap[srcId], srcId, srcIdToQsMap[srcId], destModule));
		}
		return rshlipList;
	}

	static insertSrcModulePkg(srcModulePkg, destObj, index, destMDTV) {
		var destParentModule = QuerySubjectUtils.getParentModule(destObj);
		if (!destMDTV) {
			destMDTV = "FolderType" == destObj.getObjectType() ? destObj : destParentModule.getMetadataTreeView()[0];
		}
		var objectFactory = new ObjectFactory();
		ModuleUtils.insertPackage(objectFactory, destParentModule, srcModulePkg.getIdentifier(), srcModulePkg, destMDTV, index);
	}

	static insertSrcCalculation(srcCalc, destObj, index, oldIdList, oldIdToObjectMap, newObjects) {
		var destObjType = destObj.getObjectType();
		var objectFactory = new ObjectFactory();
		var idGenerator = ModuleUtils.getIdGenerator(QuerySubjectUtils.getParentModule(destObj));
		var srcCalcClone = srcCalc.clone();
		EditModeUtils.setEditMode(srcCalcClone, null);
		var oldId = srcCalcClone.getIdentifier();
		idGenerator.generateId(srcCalcClone);
		var newId = srcCalcClone.getIdentifier();
		var parentModule = QuerySubjectUtils.getParentModule(destObj);
		parentModule.addCalculation(srcCalcClone);
		var destMDTV = "FolderType" == destObjType ? destObj : parentModule.getMetadataTreeView()[0];
		ModuleUtils._insertFolderItem(destMDTV, srcCalcClone, index, objectFactory);
		ModuleUtils._updateThisObjectReference(oldIdList, oldIdToObjectMap, srcCalcClone, destObjType);
		if (!(newId == oldId)) {
			ModuleUtils._updateOtherObjectReference(newObjects, oldId, newId, destObjType);
			oldIdToObjectMap[oldId] = srcCalcClone;
			if (oldIdList) {
				oldIdList.push(oldId);
			}
		}
		if (newObjects) {
			newObjects.push(srcCalcClone);
		}
	}

	static insertSrcFilter(srcFilter, destObj, index) {
		var destObjType = destObj.getObjectType();
		var objectFactory = new ObjectFactory();
		var idGenerator = ModuleUtils.getIdGenerator(QuerySubjectUtils.getParentModule(destObj));
		var srcFilterClone = srcFilter.clone();
		EditModeUtils.setEditMode(srcFilterClone, null);
		idGenerator.generateId(srcFilterClone);
		var parentModule = QuerySubjectUtils.getParentModule(destObj);
		parentModule.addFilter(srcFilterClone);
		var destMDTV = "FolderType" == destObjType ? destObj : parentModule.getMetadataTreeView()[0];
		ModuleUtils._insertFolderItem(destMDTV, srcFilterClone, index, objectFactory);
	}

	static insertSrcQuerySubject(srcQS, destObj, index, destMDTV, oldIdList, oldIdToObjectMap, newlyAddQs, newObjects) {
		var destParentModule = QuerySubjectUtils.getParentModule(destObj);
		var srcParentModule = QuerySubjectUtils.getParentModule(srcQS);
		var useSpecs = srcParentModule.getUseSpec();
		var asReference = useSpecs && !(useSpecs.length==0);
		if (!destMDTV) {
			destMDTV = "FolderType" == destObj.getObjectType() ? destObj : destParentModule.getMetadataTreeView()[0];
		}
		var objectFactory = new ObjectFactory();
		var idGenerator = ModuleUtils.getIdGenerator(QuerySubjectUtils.getParentModule(destObj));
		InsertUtils._insertQuerySubjectImpl(destParentModule, null, srcParentModule, asReference, destMDTV, objectFactory, oldIdList, oldIdToObjectMap, newObjects, newlyAddQs, idGenerator, index, srcQS, srcQS.getObjectType());
	}

	static insertSrcQueryItem(srcQI, destObj, index, initialIndex, oldIdList, oldIdToObjectMap, qsNewlyInsertQIMap, qsNewlyInsertQILst, targetQSs) {
		var asReference = InstanceType.REFERENCE === ModuleUtils._getParentQuerySubject(srcQI).getInstanceType();
		var destParentModule = QuerySubjectUtils.getParentModule(destObj);
		var srcParentModule = QuerySubjectUtils.getParentModule(srcQI);
		var destinationMDTV = "FolderType" == destObj.getObjectType() ? destObj : destParentModule.getMetadataTreeView()[0];
		var objectFactory = new ObjectFactory();
		var idGenerator = ModuleUtils.getIdGenerator(destParentModule);
		ModuleUtils._insertQueryItem(destObj, null, srcParentModule, asReference, destinationMDTV, index, objectFactory, oldIdList, oldIdToObjectMap, qsNewlyInsertQIMap, qsNewlyInsertQILst, idGenerator, initialIndex, srcQI, true, targetQSs);
	}

	static insertSrcRelationship(srcRel, destObj, oldIdToObjectMap) {
		var prModuleUseSpecs = QuerySubjectUtils.getParentModule(srcRel).getUseSpec();
		var asReference = !(prModuleUseSpecs.length==0);
		var destParentModule = QuerySubjectUtils.getParentModule(destObj);
		var srcParentModule = QuerySubjectUtils.getParentModule(srcRel);
		var idGenerator = ModuleUtils.getIdGenerator(destParentModule);
		var rel = srcRel.clone();
		var qsObj = oldIdToObjectMap[rel.getLeft().getRef()];
		if (qsObj) {
			rel.getLeft().setRef(qsObj.getIdentifier());
		}
		qsObj = oldIdToObjectMap[rel.getRight().getRef()];
		if (qsObj) {
			rel.getRight().setRef(qsObj.getIdentifier());
		}
		if (!ModuleUtils._findRelationshipMatch(destParentModule, rel)) {
			var qsLeft = ModuleUtils.getQuerySubjectById(rel.getLeft().getRef(), destParentModule);
			var qsRight = ModuleUtils.getQuerySubjectById(rel.getRight().getRef(), destParentModule);
			if (asReference) {
				if (!qsLeft) {
					qsLeft = ModuleUtils._getLinkedQuerySubject(rel.getLeft().getRef(), srcParentModule, destParentModule);
					if (qsLeft) {
						rel.getLeft().setRef(qsLeft.getIdentifier());
					}
				}
				if (!qsRight) {
					qsRight = ModuleUtils._getLinkedQuerySubject(rel.getRight().getRef(), srcParentModule, destParentModule);
					if (qsRight) {
						rel.getRight().setRef(qsRight.getIdentifier());
					}
				}
			}
			if (qsLeft && qsRight) {
				if (qsLeft.getInstanceType() == InstanceType.REFERENCE && qsRight.getInstanceType() == InstanceType.REFERENCE) {
					if (ModuleUtils.canMarkAsTransientRel(qsLeft, qsRight)) {
						rel.setTransientSource(TransientSourceType.MODULE);
					}
					rel.setInstanceType(InstanceType.REFERENCE);
					rel.setEditMode(EditModeType.RO);
					rel.setRef(ModuleUtils.getSourceModuleAliasIfNotDefined(null, destParentModule, srcRel) + "." + rel.getIdentifier());
				} else {
					rel.setInstanceType(null);
					rel.setRef(null);
					rel.setEditMode(null);
				}
				idGenerator.generateId(rel);
				destParentModule.addRelationship(rel);
			}
		}
		ReferenceResolver.resolveRelationship(rel, destParentModule);
	}

	static insertSrcFolderType(srcFolderType, destObj, index, destMDTV) {
		var objectFactory = new ObjectFactory();
		var idGenerator = ModuleUtils.getIdGenerator(QuerySubjectUtils.getParentModule(destObj));
		var destParentModule = QuerySubjectUtils.getParentModule(destObj);
		if (!destMDTV) {
			destMDTV = "FolderType" == destObj.getObjectType() ? destObj : destParentModule.getMetadataTreeView()[0];
		}
		ModuleUtils._insertFolder(destMDTV, srcFolderType, index, objectFactory);
		srcFolderType.setEditMode(null);
		idGenerator.generateId(srcFolderType);
		if (srcFolderType.basicGetFolderItem()) {
			srcFolderType.basicGetFolderItem().length = 0;
		}
	}

	static _insertQuerySubjectImpl(destParent, srcModuleId, srcModule, asReference, mdtvFolder, objectFactory, oldIdList, oldIdToObjectMap, newObjects, newlyAddQs, idGenerator, insertAtIndex, obj, objectType) {
		var destParentObjectType = destParent.getObjectType();
		var mdtvIndex = insertAtIndex;
		if (!("Module" == destParentObjectType)) {
			return mdtvIndex;
		}
		var module = destParent;
		var srcModuleAlias = ModuleUtils.getSourceModuleAliasIfNotDefined(srcModuleId, module, obj);
		var srcQS = obj;
		var qs = srcQS.clone();
		var oldId = qs.getIdentifier();
		if (qs.basicGetSecurityFilter()) {
			qs.getSecurityFilter().length = 0;
		}
		qs.setInstanceType(asReference ? InstanceType.REFERENCE : null);
		if (asReference) {
			EditModeUtils.setEditMode(qs, EditModeType.RO);
			if (ShortcutUtils.isShortcutValid(qs)) {
				qs.setEditMode(EditModeType.RWPARTIAL);
			} else if (ShortcutUtils.insertAsShortcut(srcQS)) {
				QuerySubjectUtils.clearEmbeddedFilter(qs);
				qs.setEditMode(EditModeType.RWPARTIAL);
				qs.setTreatAs(TreatAs.REFERENCE);
			}
		} else {
			EditModeUtils.setEditMode(qs, null);
			qs.setTreatAs(null);
		}
		QuerySubjectUtils.clearPropertyOverride(qs);
		ModuleUtils._updateQuerySubjectRef(module, qs, srcModuleAlias, srcModule, asReference, oldId);
		idGenerator.generateId(qs);
		var newId = qs.getIdentifier();
		var idForExprPrefix = qs.getIdentifier() + ".";
		for (var __i_enFor0 = 0, __exp_enFor0 = MoserObjectUtils.getQueryItems(qs), __len_enFor0 = __exp_enFor0.length;
				__i_enFor0 < __len_enFor0; ++__i_enFor0) {
			var qi = __exp_enFor0[__i_enFor0];
			if (!asReference) {
				qi.setExpression(qi.getIdentifier());
			}
			qi.setIdForExpression(idForExprPrefix + qi.getIdentifier());
		}
		module.addQuerySubject(qs);
		ModuleUtils._insertFolderItem(mdtvFolder, qs, mdtvIndex++, objectFactory);
		ModuleUtils._updateThisObjectReference(oldIdList, oldIdToObjectMap, qs, objectType);
		if (!(newId == oldId)) {
			ModuleUtils._updateOtherObjectReference(newObjects, oldId, newId, objectType);
			oldIdToObjectMap[oldId] = qs;
			if (oldIdList) {
				oldIdList.push(oldId);
			}
		}
		if (newObjects) {
			newObjects.push(qs);
			newlyAddQs.push(obj);
		}
		return mdtvIndex;
	}

	static _createNoOpRecord(srcObj) {
		return new InsertRecord(srcObj, null, null, InsertStatus.NOOP, null);
	}

	static gatherInsertActions(a0, a1, a2, a3, a4, a5, a6, a7, a8, a9) {
		var args = arguments;
		if (args.length == 9) {
			return InsertUtils.gatherInsertActions$0(a0, a1, a2, a3, a4, a5, a6, a7, a8);
		}
		return InsertUtils.gatherInsertActions$1(a0, a1, a2, a3, a4, a5, a6, a7, a8, a9);
	}

	//constructor() 
}


// WEBPACK FOOTER //
// ./src/generated/InsertUtils.js